import angular from 'angular';
import template from './templates/autocomplete-directive.tpl.html';
import selectedElemTemplate from './templates/autocomplete-selected.tpl.html';
import menuElemTemplate from './templates/autocomplete-menu.tpl.html';

import DropdownController from '../dropdown/dropdown-controller';

const MINIMUM_SEARCH = 1;

class AutocompleteController extends DropdownController {

  static get $inject() {
    return ['$scope', '$parse', '$translate', 'dropdownTemplateService', 'appendToBodyService',
      '$compile', '$log', '$timeout', '$window', '$animate', '$q', '$element', '$attrs'];
  }

  constructor($scope, $parse, $translate, dropdownTemplateService, appendToBodyService, $compile,
    $log, $timeout, $window, $animate, $q, $element, $attrs) {
    super($scope, $parse, $translate, dropdownTemplateService, appendToBodyService, $compile,
      $log, $q);

    this.$timeout = $timeout;
    this.$scope = $scope;
    this.$animate = $animate;
    this.$q = $q;
    this.$element = $element;
    this.$window = $window;
    this.$attrs = $attrs;
  }

  $onInit() {
    super.$onInit();
    this.name = 'autocomplete';
    this.dropdownElem = this.$element.children(0);
    this.templateData = {
      selected: {
        template: selectedElemTemplate,
        customSelector: 'span.selected-option'
      },
      menu: {
        template: menuElemTemplate,
        customSelector: 'a.dropdown-item-link'
      }
    };
    this.searchTerm = '';
    this.items = [];
    this.setMinimumSearch();
    this.autocompletePromiseLoading = false;
    this.autocompletePromiseError = false;
    this.scrollBarClicked = false;
    this.disabledPropertyFn = this.$parse(this.disabledProperty);
    this.noFilterResultsMessage = this.noFilterResultsMessage ||
      'components.autocomplete.noFilterResults';
    this.itemFailureMessage = this.itemFailureMessage ||
      'components.autocomplete.itemFailureMessage';
    this.minSearchMessage = this.minSearchMessage ||
      'components.autocomplete.minSearchMessage';
    this.initialize();
  }

  initialize() {
    super.initialize();

    this.ngModel.$render = () => {
      this.selectedItem = this.ngModel.$viewValue;
      this.initialSearch();
    };

    this.searchInput = this.$element[0].querySelector('input.autocomplete-search');
    const angularSearchInput = angular.element(this.searchInput);
    angularSearchInput.on('blur', () => {
      this.ngModel.$setTouched();
    });
    angularSearchInput.on('focus', () => {
      this.ngModel.$setDirty();
    });

    this.setAutoFocus(this.$attrs);
    this.$animate.enabled(this.$element, false);
  }

  setAutoFocus(attrs) {
    if (angular.isDefined(attrs.autofocus) && !this.isDisabled && !this.isReadonly) {
      this.searchShown = true;
      this.$timeout(() => this.searchInput.focus());
    }
  }

  clearSelectedItem(e) {
    super.clearSelectedItem(e);
    this.clickSelected(e);
  }

  clickSelected() {
    if (!this.isDisabled && !this.isReadonly) {
      this.searchShown = true;
      this.$timeout(() => this.searchInput.focus());
    }
  }

  isSearchShown() {
    return this.searchShown;
  }

  initialSearch() {
    if (this.keyProperty) {
      this.items = this.onSearch({
        searchTerm: this.searchTerm
      });
    }
  }

  search() {
    this.setMinimumSearch();
    if (this.minimumSearch !== 0) {
      if (!this.meetsMinSearch()) {
        this.isOpen = true;
        this.items = [];
        return;
      }
    }

    const searchResult = this.onSearch({
      searchTerm: this.searchTerm
    });
    this.isOpen = true;
    this.autocompletePromiseLoading = true;
    this.autocompletePromiseError = false;

    if (angular.isDefined(searchResult)) {
      this.$q.when(searchResult)
        .then( promiseItems => {
          promiseItems = promiseItems && promiseItems.hasOwnProperty('data') ?
            promiseItems.data : promiseItems;

          this.items = promiseItems;
        })
        .catch(() => {
          this.items = [];
          this.autocompletePromiseError = true;
        })
        .finally(() => {
          this.autocompletePromiseLoading = false;
        });
    } else {
      this.items = [];
      this.autocompletePromiseLoading = false;
      this.autocompletePromiseError = true;
      throw new Error('on-search callback is required to return an Array or Promise');
    }
  }

  noFilterResults() {
    return !this.items.length && !this.autocompletePromiseLoading && !this.autocompletePromiseError;
  }

  hasPromiseError() {
    return !this.autocompletePromiseLoading && this.autocompletePromiseError;
  }

  meetsMinSearch() {
    return this.searchTerm.length >= this.minimumSearch;
  }

  menuMouseDown(e) {
    if ((/msie|trident/i).test(this.$window.navigator.userAgent)) {
      this.scrollBarClicked = true;
    }

    e.preventDefault();
  }

  blurSearch(e) {
    if (this.scrollBarClicked) {
      this.scrollBarClicked = false;
      this.clickSelected(e);
      return;
    }
    this.scrollBarClicked = false;
    this.searchShown = false;
    this.searchTerm = '';
    this.dropdownElem.removeClass('open');
    this.isOpen = false;

    if (angular.isFunction(this.onBlur)) {
      this.onBlur({
        $event: e
      });
    }
  }

  setMinimumSearch() {
    const minimumSearch = parseInt(this.minimumSearch, 10);

    if (isNaN(minimumSearch)) {
      this.minimumSearch = MINIMUM_SEARCH;
    } else {
      this.minimumSearch = minimumSearch < 0 ? MINIMUM_SEARCH : minimumSearch;
    }
  }

  getItems() {
    return this.autocompletePromiseLoading ? [] : this.items;
  }
}

function AutocompleteDirective(dropdownTemplateService) {

  return {
    restrict: 'E',
    require: {
      ngModel: ''
    },
    scope: {},
    bindToController: {
      textProperty: '@?',
      keyProperty: '=?',
      placeholder: '@?',
      isDisabled: '=?',
      isReadonly: '=?',
      onSearch: '&',
      minimumSearch: '@',
      onBlur: '&?',
      noFilterResultsMessage: '@?',
      itemFailureMessage: '@?',
      minSearchMessage: '@?',
      disabledProperty: '@?'
    },
    controller: AutocompleteController,
    controllerAs: 'autocomplete',

    template(tElem) {
      return dropdownTemplateService.transformTemplate(tElem, template,
        'akam-autocomplete-selected', 'akam-autocomplete-option');
    }
  };
}

AutocompleteDirective.$inject = ['dropdownTemplateService'];

export default AutocompleteDirective;
